import { createFileRoute } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { BacklinkEmbed } from "@/components/BacklinkEmbed";
import { EmbedCodeBlock } from "@/components/EmbedCodeBlock";
import { SITE_URL } from "@/lib/seo";

const TITLE = "Embed DrHint Widgets — Free Live Gold & Silver Rate Embeds";
const DESC = "Copy-paste free live gold & silver rate widgets for your blog, news article or website. Live graph, interactive dashboard and compact iframe — auto-updated every 10 minutes.";

export const Route = createFileRoute("/embed-tools")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE_URL}/embed-tools` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/embed-tools` }],
  }),
  component: EmbedToolsPage,
});

function EmbedToolsPage() {
  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-6">
        <h1 className="text-3xl md:text-4xl font-bold">Embed DrHint Widgets — Free Live Gold &amp; Silver Rate Embeds</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Add a live-updating gold or silver rate widget to your blog, news article, or website in one copy-paste.
          Every widget auto-refreshes every 10 minutes and includes a visible source credit back to DrHint. Free for
          personal and commercial use — attribution link must remain visible.
        </p>
        <a
          href="/widgets"
          className="mt-4 inline-flex rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-background hover:opacity-90"
        >
          New: Developer Hub — full ready-made page embeds →
        </a>
      </section>

      {[
        { country: "pakistan" as const, label: "Pakistan" },
        { country: "india" as const, label: "India" },
      ].map(({ country, label }) => (
        <div key={country} className="mx-auto max-w-5xl px-4 pb-10">
          <h2 className="text-xl md:text-2xl font-bold mb-4">{label} — embed widgets</h2>
          <div className="space-y-6">
            <BacklinkEmbed
              variant="chart"
              title={`📰 Embed Live ${label} Gold Chart in Your News Article / Blog`}
              ctaLabel="Get embed code"
              targetUrl={`${SITE_URL}/${country}`}
              imageSrc={`${SITE_URL}/icon-512.png`}
              imageAlt={`Live 24K gold price chart — ${label} by DrHint`}
            />
            <BacklinkEmbed
              variant="widget"
              title={`🔌 Embed the Free Live ${label} Gold Dashboard on Your Website`}
              ctaLabel="Get embed code"
              targetUrl={SITE_URL}
              embedSrc={`${SITE_URL}/embed/gold/${country}`}
              height={250}
              preview={
                <iframe
                  src={`${SITE_URL}/embed/gold/${country}`}
                  style={{ width: "100%", height: 250, border: 0, borderRadius: 8 }}
                  loading="lazy"
                  title={`DrHint Live ${label} Gold Dashboard preview`}
                />
              }
            />
            <div>
              <div className="text-sm font-semibold mb-2">Compact 320×180 widget ({label})</div>
              <EmbedCodeBlock src={`${SITE_URL}/embed/gold/${country}`} width={320} height={180} />
            </div>
          </div>
        </div>
      ))}
    </Layout>
  );
}